/**
 * Coverage API Client
 *
 * Provides access to document coverage and extraction status reporting.
 */

import { apiClient } from './client';

// ============================================================
// Types
// ============================================================

export interface MimeCoverage {
  mime_type: string;
  total: number;
  searchable: number;
  failed: number;
  skipped: number;
  pending: number;
}

export interface SkipReasonCount {
  reason: string;
  count: number;
  description: string;
}

export interface FailureReasonCount {
  reason: string;
  count: number;
  description: string;
}

export interface MatterCoverageResponse {
  matter_id: string;
  matter_name: string | null;
  total_documents: number;
  searchable_count: number;
  searchable_percent: number;
  by_status: Record<string, number>;
  by_mime: MimeCoverage[];
  top_skip_reasons: SkipReasonCount[];
  top_failure_reasons: FailureReasonCount[];
  computed_at: string;
}

export interface ExtractorCapability {
  extractor_type: string;
  mime_types: string[];
  extensions: string[];
  requires_external_tool: boolean;
  tool_name: string | null;
  tool_available: boolean;
}

export interface ExtractorCapabilitiesResponse {
  extractors: ExtractorCapability[];
  supported_mimes: string[];
  supported_extensions: string[];
}

// ============================================================
// API Functions
// ============================================================

/**
 * Get coverage report for a matter.
 */
export async function getMatterCoverage(matterId: string): Promise<MatterCoverageResponse> {
  const response = await apiClient.get<MatterCoverageResponse>(`/coverage/matters/${matterId}`);
  return response.data;
}

/**
 * Get list of all extractor capabilities.
 */
export async function getExtractorCapabilities(): Promise<ExtractorCapabilitiesResponse> {
  const response = await apiClient.get<ExtractorCapabilitiesResponse>('/coverage/extractors');
  return response.data;
}

/**
 * Check coverage service health.
 */
export interface CoverageHealthResponse {
  status: string;
  extractors_registered: number;
  supported_mimes: number;
  supported_extensions: number;
}

export async function getCoverageHealth(): Promise<CoverageHealthResponse> {
  const response = await apiClient.get<CoverageHealthResponse>('/coverage/health');
  return response.data;
}

/**
 * Export coverage report as CSV.
 * Triggers a file download in the browser.
 */
export async function exportCoverageCSV(matterId: string): Promise<void> {
  const response = await apiClient.get<Blob>(`/coverage/matters/${matterId}/export`);
  
  // Extract filename from Content-Disposition header or use default
  const contentDisposition = response.headers.get('content-disposition');
  let filename = `coverage_${matterId}.csv`;
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
    if (filenameMatch) {
      filename = filenameMatch[1];
    }
  }
  
  // The apiClient.parseResponse returns a Blob for non-JSON responses
  const blob = response.data instanceof Blob 
    ? response.data 
    : new Blob([response.data as BlobPart], { type: 'text/csv' });
  
  // Trigger download
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

// ============================================================
// Export
// ============================================================

export const coverageApi = {
  getMatterCoverage,
  getExtractorCapabilities,
  getCoverageHealth,
  exportCoverageCSV,
};

export default coverageApi;
